import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { googleAdsService, GoogleAdsAccount } from '@/services/google-ads.service';
import { GOOGLE_OBJECTIVES, objectiveRoute } from '@/components/google-ads/GoogleAdsObjectives';
import { ArrowRight, Check, Globe, Info, Lock } from 'lucide-react';
import clsx from 'clsx';

/** Google's brand mark — inline SVG so it needs no asset pipeline. */
const GoogleMark = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12c-.2 2-1.5 5-4.4 7l6.7 5.2c4-3.7 6.8-9.1 6.8-15.8z" />
    <path fill="#34A853" d="M24 46c6 0 11-2 14.3-5.4l-6.7-5.2c-1.8 1.3-4.3 2.2-7.6 2.2-5.9 0-10.8-3.9-12.6-9.2l-7 5.4C7.8 41.1 15.3 46 24 46z" />
    <path fill="#FBBC05" d="M11.4 28.4A13.6 13.6 0 0 1 10.7 24c0-1.5.3-3 .7-4.4l-7-5.4A22 22 0 0 0 2 24c0 3.5.9 6.9 2.4 9.8l7-5.4z" />
    <path fill="#EA4335" d="M24 10.2c4.2 0 7 1.8 8.6 3.3l6.3-6.1C35 3.9 30 2 24 2 15.3 2 7.8 6.9 4.4 14.2l7 5.4C13.2 14.1 18.1 10.2 24 10.2z" />
  </svg>
);

/**
 * "What's your campaign objective?" — the entry point for Google Ads campaign
 * creation. Every objective maps to a real advertisingChannelType; the ones we
 * can't fully build yet say exactly what's missing instead of a bare
 * "Coming soon".
 */
export default function GoogleAdsObjectivePicker() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState<GoogleAdsAccount[] | null>(null);

  useEffect(() => {
    googleAdsService.list().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const connected = (accounts ?? []).filter((a) => a.customerId);

  return (
    <>
      <Topbar
        title="New Google Ads campaign"
        breadcrumb="Google Ads / Campaigns"
        description="Choose an objective — we'll set the campaign type and bidding to match."
        back={
          <button
            onClick={() => nav('/google-ads/campaigns')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            aria-label="Back to campaigns"
          >
            <ArrowRight size={16} className="rotate-180" />
          </button>
        }
      />

      <div className="px-7 pb-24 pt-6">
        {accounts === null ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-44 rounded-2xl" />)}
          </div>
        ) : connected.length === 0 ? (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Authorise Google and attach a customer ID, then come back to build campaigns."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
          />
        ) : (
          <>
            <div className="mb-5 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface">
                <GoogleMark />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-text-primary">Choose your objective</h2>
                <p className="text-xs text-text-muted">
                  Google Ads splits into campaign types — Search, Video, Display, App, Performance Max
                  and Shopping. Your objective picks the right one and the matching workspace.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {GOOGLE_OBJECTIVES.map((o) => {
                const Icon = o.icon;
                const blocked = !!o.blocked;
                return (
                  <Card
                    key={o.key}
                    className={clsx(
                      'flex flex-col transition-all',
                      blocked
                        ? 'opacity-75'
                        : 'cursor-pointer hover:-translate-y-0.5 hover:border-primary hover:shadow-elevated',
                    )}
                    onClick={blocked ? undefined : () => nav(objectiveRoute(o))}
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <span className={clsx(
                        'flex h-10 w-10 items-center justify-center rounded-xl',
                        blocked ? 'bg-surface-alt text-text-muted' : 'bg-primary-soft text-primary',
                      )}>
                        <Icon size={18} />
                      </span>
                      <span className="rounded-full border border-border px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">
                        {o.channelType.replace(/_/g, ' ')}
                      </span>
                    </div>

                    <h3 className="text-sm font-semibold text-text-primary">{o.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-text-secondary">{o.desc}</p>

                    {/* What the wizard will actually create — no guessing. */}
                    <ul className="mt-3 space-y-1">
                      {o.builds.map((b) => (
                        <li key={b} className="flex items-start gap-1.5 text-2xs text-text-muted">
                          <Check size={11} className={clsx('mt-0.5 shrink-0', blocked ? 'text-text-muted' : 'text-success')} />
                          {b}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-3">
                      {blocked ? (
                        <div className="flex items-start gap-1.5 rounded-lg bg-surface-alt/60 px-2.5 py-2">
                          <Lock size={11} className="mt-0.5 shrink-0 text-warning" />
                          <span className="text-[11px] leading-snug text-text-muted">{o.blocked}</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {o.requires && (
                            <div className="flex items-start gap-1.5 rounded-lg bg-surface-alt/60 px-2.5 py-2">
                              <Info size={11} className="mt-0.5 shrink-0 text-info" />
                              <span className="text-[11px] leading-snug text-text-muted">Needs: {o.requires}</span>
                            </div>
                          )}
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                            Continue <ArrowRight size={13} />
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-info/30 bg-info-soft/40 px-4 py-3">
              <Info size={14} className="mt-0.5 shrink-0 text-info" />
              <p className="text-xs leading-relaxed text-text-secondary">
                Campaigns are created <strong>paused</strong> so you can review targeting and budget before
                anything spends. Mutations need a Standard-Access Google Ads developer token on the
                connected account.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
